import styled from "@emotion/styled";
import { Chart, ArcElement, Tooltip, Legend } from "chart.js";
import { Doughnut } from "react-chartjs-2";
import { MoreVertical } from "react-feather";
import { useTheme } from "@mui/material/styles";
import useComplaint from "../../../hooks/useComplaint";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import useWindowDimensions from "../../../utils/windowDimensions";
import { green, red, orange, pink, purple, blue, grey, amber, teal } from "@mui/material/colors";
import { useDialog } from "../../../contexts/DialogContext";
import { getTimeStamp } from "../../../utils/common";
import Upload from "../components/Upload";
import {
  Card as MuiCard,
  CardContent as MuiCardContent,
  CardHeader,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell as MuiTableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

Chart.register(ArcElement, Tooltip, Legend);

const Card = styled(MuiCard)`
  margin-bottom: 24px;
`;

const CardContent = styled(MuiCardContent)`
  &:last-child {
    padding-bottom: ${(props) => props.theme.spacing(2)};
  }
`;

const ChartWrapper = styled.div`
  height: 200px;
  position: relative;
`;

const DoughnutInner = styled.div`
  width: 100%;
  position: absolute;
  top: 50%;
  left: 0;
  margin-top: -22px;
  text-align: center;
  z-index: 0;
`;

const TableCell = styled(MuiTableCell)`
  padding: 8px;
`;

const AssignedText = styled.span`
  color: ${() => blue[500]};
  font-weight: bold;
`;

const ReAssignedText = styled.span`
  color: ${() => orange[500]};
  font-weight: bold;
`;

const OpenText = styled.span`
  color: ${() => red[500]};
  font-weight: bold;
`;

const InProgressText = styled.span`
  color: ${() => amber[500]};
  font-weight: bold;
`;
const ResolvedText = styled.span`
  color: ${() => green[500]};
  font-weight: bold;
`;
const ClosedText = styled.span`
  color: ${() => grey[600]};
  font-weight: bold;
`;

const Stats = () => {
  const stheme = useTheme();
  const { t } = useTranslation();
  const { state } = useLocation();

  const TotalText = styled.span`
    color: ${() => stheme.palette.secondary.main};
    font-weight: bold;
  `;

  const currentPayload = {
    filters: [
      {
        fieldName: "complaintId",
        fieldValue: state?.id,
        op: "eq",
        dataType: "int",
        condition: "and",
        isJoin: true,
      },
    ],
    pagination: {
      pageNumber: 1,
      pageSize: 100,
    },
    sortFields: [
      {
        field: "complaintStatsId",
        sort: "DESC",
      },
    ],
  };

  const [payload, setPayload] = useState(currentPayload);
  const { getAllComplaintStats, complaintStats, isLoading, downloadFile } = useComplaint();
  const { openDialog } = useDialog();

  const fetchData = async () => {
    await getAllComplaintStats(payload);
  };

  useEffect(() => {
    fetchData();
  }, [payload]);

  const data = {
    labels: ["Total", "Open", "Assigned", "Reassigned", "In Progress", "Resolved", "Closed"],
    datasets: [
      {
        data: [
          complaintStats.total,
          complaintStats.open,
          complaintStats.assigned,
          complaintStats.reAssigned,
          complaintStats.inProgress,
          complaintStats.resolved,
          complaintStats.closed,
        ],
        backgroundColor: [
          stheme.palette.secondary.main,
          red[500], // Open → Red
          blue[500], // Assigned → Blue
          orange[500], // Re-Assigned → Orange
          amber[500], // In Progress → Amber (yellow-orange)
          green[500], // Resolved → Green
          grey[600], // Closed → Neutral Grey
        ],
        borderWidth: 5,
        borderColor: stheme.palette.background.paper,
      },
    ],
  };

  const options = {
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    cutout: "80%",
  };

  return (
    <Card>
      <CardHeader
        action={
          <IconButton aria-label="settings" size="large">
            <MoreVertical />
          </IconButton>
        }
        title={t("complaint_stats")}
      />

      <CardContent theme={stheme}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <ChartWrapper>
              <DoughnutInner>
                <Typography variant="h4">{complaintStats.total}</Typography>
                <Typography variant="caption">Total</Typography>
              </DoughnutInner>
              <Doughnut data={data} options={options} />
            </ChartWrapper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries({
                  Total: complaintStats.total,
                  Open: complaintStats.open,
                  Assigned: complaintStats.assigned,
                  Reassigned: complaintStats.reAssigned,
                  inProgress: complaintStats.inProgress,
                  Resolved: complaintStats.resolved,
                  Closed: complaintStats.closed,
                }).map(([status, count]) => (
                  <TableRow key={status}>
                    <TableCell component="th" scope="row">
                      {status}
                    </TableCell>
                    <TableCell align="right">
                      {status === "Total" ? (
                        <TotalText>{count}</TotalText>
                      ) : status === "Assigned" ? (
                        <AssignedText>{count}</AssignedText>
                      ) : status === "Open" ? (
                        <OpenText>{count}</OpenText>
                      ) : status === "Reassigned" ? (
                        <ReAssignedText>{count}</ReAssignedText>
                      ) : status === "inProgress" ? (
                        <InProgressText>{count}</InProgressText>
                      ) : status === "Resolved" ? (
                        <ResolvedText>{count}</ResolvedText>
                      ) : status === "Closed" ? (
                        <ClosedText>{count}</ClosedText>
                      ) : (
                        <GreenText>{count}</GreenText>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default Stats;
